import "bulma/css/bulma.min.css";
import "@fortawesome/fontawesome-free/js/all";
import _ from "lodash";
import moment from "moment-timezone";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import "./index.css";
import { createEvent } from "ics";
import axios from "axios";
import Loading from "components/Shared/Loading";
import { DEFAULT_TIMEZONE } from "../../constants";
import { HospitalLogo } from "../Shared/HospitalLogo";

const replaceAll = (str, searchValue, replcaeValue) => {
  let string = str;
  while (string.indexOf(searchValue) !== -1) {
    string = string.replace(searchValue, replcaeValue);
  }
  return string;
};

const generateDescription = async ({
  appointmentType,
  patientName,
  startYear,
  startMonth,
  startDay,
  time,
  practitionerVideoLink,
  practitionerVideoId,
}) => {
  const template = `Telehealth appointment for [patient name] has been confirmed.

The appointment is booked on [Time] - [Day]/[Month]/[Year].`;

  const aptTypeMapping = {
    atGpClinic: "GP Clinic",
    atHome: "Home",
  };

  return template
    .replace("[patient name]", patientName)
    .replace("[Time]", time)
    .replace("[Day]", startDay)
    .replace("[Month]", startMonth)
    .replace("[Year]", startYear)
    .replace("[Practitioner Video Link]", practitionerVideoLink)
    .replace("[Practitioner Video ID]", practitionerVideoId)
    .replace("[Home/GP Clinic]", aptTypeMapping[appointmentType]);
};

const generateCalendarEvent = async ({
  clientTimezone,
  appointmentType,
  patientName,
  startDateTime,
  endDateTime,
  practationerName,
}) => {
  // Everything on this page is the patient's: the description, the .ics start
  // and the Google link all read in their zone. Converting to device-local
  // time here put the first two on whatever device opened the link and only
  // the Google link in the patient's zone, so the three could disagree.
  const startDate = moment(startDateTime, "YYYY-MM-DDTHH:mm:ssZ").tz(clientTimezone);
  const endDate = moment(endDateTime, "YYYY-MM-DDTHH:mm:ssZ").tz(clientTimezone);

  const duration = moment.duration(endDate.diff(startDate));
  const [startYear, startMonth, startDay, time] = [
    startDate.year(),
    startDate.month() + 1,
    startDate.date(),
    startDate.format("HH:mm z"),
  ];

  const description = await generateDescription({
    appointmentType,
    patientName,
    startYear,
    startMonth,
    startDay,
    time,
  });

  // The .ics start goes out as UTC with an explicit output type. As a bare
  // numeric array it was a floating time: correct in the description, but a
  // device in another zone would place the downloaded event an hour off.
  const startUtc = startDate.clone().utc();
  const event = {
    start: [startUtc.year(), startUtc.month() + 1, startUtc.date(), startUtc.hour(), startUtc.minute()],
    startInputType: "utc",
    startOutputType: "utc",
    dates: `${startDate.format("YYYYMMDDTHHmmSSZ")}/${endDate.format("YYYYMMDDTHHmmSSZ")}`,
    duration: { hours: duration.hours(), minutes: duration.minutes() },
    title: `Appointment with Dr ${practationerName} from Telecare`,
    description,
    busyStatus: "BUSY",
    organizer: { name: "Telecare", email: "hello@telecare.com.au" },
    attendees: [],
    alarms: [
      {
        action: "audio",
        trigger: { days: 1, before: true },
        repeat: 0,
        attachType: "VALUE=URI",
        attach: "Glass",
      },
      {
        action: "audio",
        trigger: { minutes: 10, before: true },
        repeat: 0,
        attachType: "VALUE=URI",
        attach: "Glass",
      },
    ],
  };
  return event;
};

const AppointmentConfrimFrom = () => {
  const { id: caId } = useParams();
  const [event, setEvent] = useState({});
  const [icsData, setIcsData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const getData = async () => {
      const { data } = await axios.get(`${process.env.REACT_APP_GENERATE_CALENDAR_EVENT}/${caId}`);
      const eventData = _.get(data, "eventData", null);
      if (_.isNil(eventData)) {
        return;
      }
      // Resolved once, before anything parses a date with it: a legacy
      // response with no clientTimezone used to reach the generator as
      // undefined, so only the Google link - not the .ics or the description -
      // got the fallback.
      const clientTimezone = _.get(eventData, "clientTimezone") || DEFAULT_TIMEZONE;
      const { dates, ...event } = await generateCalendarEvent({ ...eventData, clientTimezone });
      const { value: icsData, error } = await createEvent(event);
      if (error) {
        return;
      }
      setEvent({ dates, ...event, clientTimezone });
      setIcsData(encodeURIComponent(icsData));
      setIsLoading(false);
    };

    getData();
  }, [caId]);

  return isLoading ? (
    <Loading />
  ) : (
    <div className="columns">
      <div className="column is-one-third is-offset-4">
        <div className="section content has-text-centered">
          <HospitalLogo />
          <div className="calendar-title">Add to calendar</div>
          <p className="has-text-left">{_.get(event, "description")}</p>
          <p className="has-text-left">
            Please choose one of the options below to sync the appointment with the e-calendar service you use.
          </p>
          <div className="calendar-container">
            <a
              className="calendar-modal-link__button"
              aria-label="Save to Google calendar"
              href={`https://calendar.google.com/calendar/event?text=${_.get(event, "title")}&details=${_.get(
                event,
                "description"
              )}&action=TEMPLATE&dates=${_.get(event, "dates")}&ctz=${_.get(
                event,
                "clientTimezone",
                DEFAULT_TIMEZONE
              )}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="calendar-modal-link__icon">
                <i className="fab fa-google"></i>
              </span>
              <span className="calendar-modal-link__text">Google calendar</span>
            </a>

            <a
              className="calendar-modal-link__button"
              aria-label="Save to Apple calendar"
              href={`data:text/calendar;charset=utf-8,${icsData}`}
              download={`${replaceAll(_.get(event, "title"), " ", "_")}`}
            >
              <span className="calendar-modal-link__icon">
                <i className="fab fa-apple"></i>
              </span>
              <span className="calendar-modal-link__text">Apple calendar</span>
            </a>
            <a
              className="calendar-modal-link__button"
              aria-label="Save to Outlook calendar"
              href={`data:text/calendar;charset=utf-8,${icsData}`}
              download={`${replaceAll(_.get(event, "title"), " ", "_")}`}
            >
              <span className="calendar-modal-link__icon">
                <i className="far fa-envelope"></i>
              </span>
              <span className="calendar-modal-link__text">Outlook calendar</span>
            </a>

            <a
              className="calendar-modal-link__button"
              aria-label="Save to Other"
              href={`data:text/calendar;charset=utf-8,${icsData}`}
              download={`${replaceAll(_.get(event, "title"), " ", "_")}`}
            >
              <span className="calendar-modal-link__icon">
                <i className="far fa-calendar-check"></i>
              </span>
              <span className="calendar-modal-link__text">Other</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentConfrimFrom;
